/**
 * Who may propagate this cultivar, and on what terms.
 *
 * Eligibility and price are one decision, so this is one form. Admitting a
 * propagator without setting what they pay would leave a nursery listed at a
 * rate nobody chose — which is how a cultivar gets sold cheaply by accident.
 *
 * Different propagators pay different rates by design. An orchard propagating
 * its own trees carries audit obligations a commercial nursery does not, and
 * the rights holder prices that separately; `propagator_kind` is on the row so
 * a rate that looks odd can be read rather than guessed at.
 *
 * The conditions captured here are the LICENSOR's — what a propagator must
 * agree to in order to produce. They are not a nursery's terms of sale to an
 * orchard: that is a private contract between those two, carrying the
 * nursery's own document, and Factree is not a party to it.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Sprout, Loader2, Plus, X, Building2, Search, AlertCircle, Home,
} from 'lucide-react';
import Button from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import cultivarService, {
  type PropagatorTerms,
  type PropagatorTermsInput,
} from '../../services/cultivar';
import organizationService from '../../services/organization';
import {
  MATERIAL_KIND_LABELS,
  SOURCE_TYPE_LABELS,
  type DeclaredMaterialSource,
} from '../../types/propagation';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';

interface Props {
  cultivarId: number;
  /** Only a licensor on this cultivar may admit or re-price a propagator. */
  canManage: boolean;
}

interface OrgResult {
  id: number;
  name: string;
  stakeholder_types: string[];
}

const KIND_LABELS: Record<string, string> = {
  nursery: 'Nursery',
  self_propagating_orchard: 'Orchard propagating its own',
};

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  suspended: 'bg-amber-50 text-amber-900 border-amber-200',
  withdrawn: 'bg-neutral-100 text-neutral-600 border-neutral-200',
};

function feeLabel(row: PropagatorTerms): string {
  if (row.per_tree_fee === null || row.per_tree_fee === undefined) {
    return 'Programme rate';
  }
  const n = typeof row.per_tree_fee === 'string' ? parseFloat(row.per_tree_fee) : row.per_tree_fee;
  if (!Number.isFinite(n)) return 'Programme rate';
  return `${row.currency} ${n.toFixed(2)} / tree`;
}

export default function PropagatorTermsPanel({ cultivarId, canManage }: Props) {
  const orgTz = useOrgTimezone();

  const [rows, setRows] = useState<PropagatorTerms[]>([]);
  // Where each propagator says its material comes from. Only a rights holder
  // may see these — the server refuses anyone else — and they are the reason
  // propagators are asked to declare: this is where the double check happens.
  const [sources, setSources] = useState<DeclaredMaterialSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [orgQuery, setOrgQuery] = useState('');
  const [orgResults, setOrgResults] = useState<OrgResult[]>([]);
  const [orgOpen, setOrgOpen] = useState(false);
  const [orgLoading, setOrgLoading] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<number | undefined>();
  const [selectedOrgName, setSelectedOrgName] = useState('');
  const [kind, setKind] = useState<'nursery' | 'self_propagating_orchard'>('nursery');
  const [perTreeFee, setPerTreeFee] = useState('');
  const [conditions, setConditions] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await cultivarService.listPropagatorTerms(cultivarId);
      if (!res.success) {
        setError(res.error?.message || 'Could not load propagator terms');
        return;
      }
      setRows(res.data || []);
      if (canManage) {
        try {
          const srcRes = await cultivarService.listMaterialSources(cultivarId);
          setSources(srcRes.success ? srcRes.data || [] : []);
        } catch {
          setSources([]);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load propagator terms');
    } finally {
      setLoading(false);
    }
  }, [cultivarId, canManage]);

  useEffect(() => { load(); }, [load]);

  const sourcesByPropagator = useMemo(() => {
    const map = new Map<number, DeclaredMaterialSource[]>();
    for (const s of sources) {
      const list = map.get(s.propagator_org_id) || [];
      list.push(s);
      map.set(s.propagator_org_id, list);
    }
    return map;
  }, [sources]);

  // A self-propagating orchard is an orchard, not a nursery, so the search has
  // to follow the kind being granted or the right organisation never appears.
  useEffect(() => {
    if (!orgOpen) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setOrgLoading(true);
      try {
        const res = await organizationService.searchOrganizations({
          name: orgQuery.trim() || undefined,
          stakeholder_type: kind === 'nursery' ? 'nursery' : 'orchard',
        });
        if (cancelled) return;
        setOrgResults(res.success && res.data ? (res.data as OrgResult[]) : []);
      } catch {
        if (!cancelled) setOrgResults([]);
      } finally {
        if (!cancelled) setOrgLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [orgQuery, orgOpen, kind]);

  const resetForm = () => {
    setSelectedOrgId(undefined);
    setSelectedOrgName('');
    setOrgQuery('');
    setKind('nursery');
    setPerTreeFee('');
    setConditions('');
    setValidFrom('');
    setValidTo('');
    setShowForm(false);
  };

  const submit = async () => {
    if (!selectedOrgId) {
      setError('Pick the organisation being admitted');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const input: PropagatorTermsInput = {
        propagator_org_id: selectedOrgId,
        propagator_kind: kind,
        // Blank inherits the programme rate. Typing 0 is a decision to charge
        // nothing, and the two must not collapse into each other.
        per_tree_fee: perTreeFee.trim() === '' ? null : Number(perTreeFee),
        conditions: conditions.trim() || undefined,
        valid_from: validFrom || null,
        valid_to: validTo || null,
      };
      const res = await cultivarService.setPropagatorTerms(cultivarId, input);
      if (!res.success) throw new Error(res.error?.message || 'Could not save terms');
      resetForm();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save terms');
    } finally {
      setSubmitting(false);
    }
  };

  const withdraw = async (row: PropagatorTerms) => {
    setError(null);
    try {
      const res = await cultivarService.withdrawPropagatorTerms(cultivarId, row.propagator_org_id);
      if (!res.success) throw new Error(res.error?.message || 'Could not withdraw');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not withdraw');
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div className="min-w-0">
          <CardTitle>Who may propagate this</CardTitle>
          <p className="text-xs text-neutral-500 mt-1">
            Admitting a propagator sets what they pay and what they agree to, in the same step.
            Rates may differ between them.
          </p>
        </div>
        {canManage && !showForm && (
          <Button variant="outline" size="sm" onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4" />
            <span className="ml-1.5">Admit</span>
          </Button>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {error && (
          <div className="flex items-start gap-2 text-sm text-red-800 bg-red-50 border border-red-200 rounded p-3">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span className="break-words">{error}</span>
          </div>
        )}

        {showForm && (
          <div className="border border-neutral-200 rounded-lg p-4 space-y-4 bg-neutral-50">
            <div className="space-y-1">
              <label className="block text-sm font-semibold text-neutral-700">Propagator type</label>
              <select
                value={kind}
                onChange={e => {
                  setKind(e.target.value as typeof kind);
                  // The register being searched changes with the kind, so a
                  // selection made against the other one is no longer valid.
                  setSelectedOrgId(undefined);
                  setSelectedOrgName('');
                  setOrgQuery('');
                }}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="nursery">Nursery — propagates to sell</option>
                <option value="self_propagating_orchard">Orchard propagating its own trees</option>
              </select>
              {kind === 'self_propagating_orchard' && (
                <p className="text-xs text-amber-700">
                  A grower producing for its own blocks, by top-working or an internal nursery.
                  Usually priced above a nursery rate — the auditing falls to you.
                </p>
              )}
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-semibold text-neutral-700">
                {kind === 'nursery' ? 'Nursery' : 'Orchard'}
              </label>
              {selectedOrgId ? (
                <div className="flex items-center justify-between px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <span className="flex items-center gap-2 min-w-0">
                    <Building2 className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    <span className="text-sm font-medium text-blue-900 truncate">{selectedOrgName}</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => { setSelectedOrgId(undefined); setSelectedOrgName(''); }}
                    className="text-blue-400 hover:text-blue-600 flex-shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                    <input
                      className="w-full pl-9 pr-3 py-2 border border-neutral-300 rounded-lg text-sm"
                      placeholder={kind === 'nursery' ? 'Search nurseries…' : 'Search orchards…'}
                      value={orgQuery}
                      onChange={e => { setOrgQuery(e.target.value); setOrgOpen(true); }}
                      onFocus={() => setOrgOpen(true)}
                      onBlur={() => setTimeout(() => setOrgOpen(false), 200)}
                    />
                    {orgLoading && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-neutral-400" />
                    )}
                  </div>
                  {orgOpen && (
                    <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                      {orgResults.length > 0 ? (
                        orgResults.map(o => (
                          <button
                            key={o.id}
                            type="button"
                            className="w-full text-left px-4 py-2.5 hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0"
                            onMouseDown={e => {
                              e.preventDefault();
                              setSelectedOrgId(o.id);
                              setSelectedOrgName(o.name);
                              setOrgOpen(false);
                            }}
                          >
                            <p className="text-sm font-medium text-neutral-900">{o.name}</p>
                          </button>
                        ))
                      ) : (
                        <p className="px-4 py-3 text-sm text-neutral-500">
                          No {kind === 'nursery' ? 'nursery' : 'orchard'} organisations match.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1">
                <label className="block text-sm font-semibold text-neutral-700">Fee per tree</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Programme rate"
                  value={perTreeFee}
                  onChange={e => setPerTreeFee(e.target.value)}
                />
                <p className="text-xs text-neutral-500">
                  Leave blank to use the programme rate. Enter 0 to charge nothing — not the
                  same thing.
                </p>
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-semibold text-neutral-700">From</label>
                <input
                  type="date"
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                  value={validFrom}
                  onChange={e => setValidFrom(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-semibold text-neutral-700">Until</label>
                <input
                  type="date"
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                  value={validTo}
                  onChange={e => setValidTo(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-semibold text-neutral-700">Your conditions</label>
              <textarea
                rows={3}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm resize-none"
                placeholder="Audit frequency, reporting, propagation method, territory…"
                value={conditions}
                onChange={e => setConditions(e.target.value)}
              />
              <p className="text-xs text-neutral-500">
                What this propagator must agree to in order to produce. Not their terms of sale —
                those are between them and their customer.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button variant="primary" size="sm" onClick={submit} disabled={submitting}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save terms'}
              </Button>
              <Button variant="outline" size="sm" onClick={resetForm} disabled={submitting}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-neutral-500 py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-neutral-500 py-2">
            Nobody may propagate this cultivar yet. Until somebody is admitted here, it cannot be
            listed or ordered.
          </p>
        ) : (
          <div className="border border-neutral-200 rounded-lg divide-y divide-neutral-100">
            {rows.map(row => (
              <div key={row.id} className="p-3 space-y-2">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-neutral-900 flex items-center gap-1.5 flex-wrap">
                      {row.propagator_kind === 'self_propagating_orchard'
                        ? <Home className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
                        : <Sprout className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />}
                      <span className="break-words">{row.propagator_name}</span>
                    </p>
                    <p className="text-xs text-neutral-500">{KIND_LABELS[row.propagator_kind]}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-sm font-semibold tabular-nums">{feeLabel(row)}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[row.status]}`}>
                      {row.status}
                    </span>
                  </div>
                </div>

                {(row.valid_from || row.valid_to) && (
                  <p className="text-xs text-neutral-500 tabular-nums">
                    {row.valid_from ? formatLocalDateInTz(row.valid_from, orgTz) : 'no start'}
                    {' — '}
                    {row.valid_to ? formatLocalDateInTz(row.valid_to, orgTz) : 'no end'}
                  </p>
                )}

                {row.conditions && (
                  <p className="text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 rounded p-2 whitespace-pre-wrap break-words">
                    {row.conditions}
                  </p>
                )}

                {canManage && (sourcesByPropagator.get(row.propagator_org_id) || []).length > 0 && (
                  <div className="text-xs text-neutral-600">
                    <p className="font-semibold text-neutral-700">Declared material sources</p>
                    <ul className="space-y-0.5">
                      {(sourcesByPropagator.get(row.propagator_org_id) || []).map(s => (
                        <li key={s.id} className="break-words">
                          {MATERIAL_KIND_LABELS[s.material_kind]} — {SOURCE_TYPE_LABELS[s.source_type]}: {s.source_name}
                          {s.source_reference ? ` · ${s.source_reference}` : ''}
                          {' '}
                          <span className="text-neutral-400">
                            ({s.declared_on === 'offer'
                              ? `offer, season ${s.offer_season_year}`
                              : `self-propagation, season ${s.request_season_year}`})
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {canManage && row.status !== 'withdrawn' && (
                  <button
                    type="button"
                    onClick={() => withdraw(row)}
                    className="text-xs text-red-600 underline"
                  >
                    Withdraw eligibility
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
